import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Input } from 'src/registry/ui/Input';
import React from 'react';
import { DiscountPolicyFormSchemaValues } from 'src/features/discountPolicies/utils/formSchema';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS, CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import { useListServicePlans } from 'src/features/servicePlans/api/useListServicePlans';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { useListServicePlanItems } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { CURRENCY } from 'src/types';
import { DiscountPolicy } from 'src/features/discountPolicies/utils/types';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';

type DiscountPolicyFormProps = {
    form: UseFormReturn<DiscountPolicyFormSchemaValues>;
    onSubmit: (values: DiscountPolicyFormSchemaValues) => void;
    selectedDiscountPolicy?: DiscountPolicy;
};

function DiscountPolicyForm({ onSubmit, form, selectedDiscountPolicy }: DiscountPolicyFormProps) {
    const { formatMessage } = useIntl();
    const [displayAmount, setDisplayAmount] = React.useState('');
    const [searchServicePlan, setSearchServicePlan] = React.useState<string>('');

    const { manual_valid_duration, service_plans, amount, percent, valid_from } = form.watch();

    const nextDayFromValidFrom = new Date(valid_from);
    nextDayFromValidFrom.setDate(nextDayFromValidFrom.getDate() + 1);

    const { data: servicePlans } = useListServicePlans({
        search: searchServicePlan,
    });
    const { data: servicePlanItems } = useListServicePlanItems({
        service_plan: service_plans ?? [],
    });

    const isInEditMode = !!selectedDiscountPolicy;

    const isAmountDisabled =
        (percent !== undefined && percent !== '') || (isInEditMode && selectedDiscountPolicy?.percent !== null);
    const isPercentDisabled = amount !== undefined || (isInEditMode && selectedDiscountPolicy?.amount !== null);

    React.useEffect(() => {
        if (amount != null) {
            setDisplayAmount(String(amount / 100));
        } else {
            setDisplayAmount('');
        }
    }, [amount]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="name"
                type="text"
                label={formatMessage(commonMessages.name)}
            />
            <TextareaFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(generalMessages.description)}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDate={{ before: new Date() }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDate={{ before: nextDayFromValidFrom }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...discountPoliciesMessages.amount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(discountPoliciesMessages.amount)}...`}
                                    value={displayAmount}
                                    onBlur={() => {
                                        if (!displayAmount) return;

                                        const numeric = parseFloat(displayAmount);
                                        if (!isNaN(numeric)) {
                                            setDisplayAmount(numeric.toFixed(2));
                                        }
                                    }}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayAmount(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                            setDisplayAmount('');
                                        }
                                    }}
                                    disabled={isAmountDisabled}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <InputFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="percent"
                required={false}
                label={formatMessage(discountPoliciesMessages.percent)}
                pattern={/^-?\d{0,20}(?:\.\d{0,4})?$/}
                disabled={isPercentDisabled}
            />
            <SelectFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
            />
            <SelectFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="manual_valid_duration"
                isBoolean={true}
                label={formatMessage(discountPoliciesMessages.manualValidDuration)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />

            {manual_valid_duration !== undefined && !manual_valid_duration && (
                <InputFormField<DiscountPolicyFormSchemaValues>
                    form={form}
                    name="valid_duration"
                    required={false}
                    label={formatMessage(discountPoliciesMessages.validDuration)}
                    type="text"
                    pattern={/^\d+$/}
                />
            )}
            <InputFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="max_applications"
                type="number"
                required={false}
                label={formatMessage(discountPoliciesMessages.maxApplications)}
            />
            <InputFormField<DiscountPolicyFormSchemaValues>
                form={form}
                name="max_applications_per_customer"
                type="number"
                required={false}
                label={formatMessage(discountPoliciesMessages.maxApplicationsPerCustomer)}
            />
            <SearchSelectFormField
                name="service_plans"
                control={form.control}
                label={formatMessage(servicePlansMessages.servicePlans)}
                items={servicePlans?.results ?? []}
                clearable
                placeholder={`${formatMessage(servicePlansMessages.servicePlans)}...`}
                searchPlaceholder={formatMessage(servicePlansMessages.searchServicePlan)}
                emptyTitle={formatMessage(servicePlansMessages.noServicePlan)}
                emptyDescription={formatMessage(servicePlansMessages.noServicePlanFound)}
                getItemValue={(plan) => plan.id}
                getItemLabel={(plan) => plan.name}
                getItemKeywords={(plan) => [plan.name]}
                loadOptions={setSearchServicePlan}
                required={false}
                isMultiSelect={true}
            />
            <FormField
                control={form.control}
                name="service_plan_items"
                render={({ field }) => {
                    const fieldData = field.value
                        ?.map((user) => servicePlanItems?.results.find((p) => p.id === user)?.name)
                        .join(', ');
                    const allValues = servicePlanItems?.results.map((plan) => plan.id);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...servicePlanItemsMessages.servicePlanItems} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect
                                        value={(field.value ?? []) as string[]}
                                        onChange={(values) => field.onChange(values)}
                                    >
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl
                                                className="-mr-4 w-full"
                                                disabled={service_plans === undefined}
                                            >
                                                {fieldData}
                                                {(!fieldData || fieldData?.length == 0) && (
                                                    <span className="text-gray-400">
                                                        {`${formatMessage(servicePlanItemsMessages.servicePlanItems)}...`}
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {servicePlanItems?.results.map((plan) => (
                                                <MultiSelectOption key={plan.id} selected={plan.id}>
                                                    <div className="flex flex-row justify-between">{plan.name}</div>
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
}

export default DiscountPolicyForm;
